import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, ChevronDown, Menu, X, ShieldCheck, Clock, ArrowUpRight, CalendarCheck } from "lucide-react";
import { SERVICES, SERVICE_GROUPS } from "@/data/services";
import { PHONE, PHONE_TEL, HOURS, bookingUrl } from "@/data/site";
import { ServiceIcon } from "./ServiceIcon";

const TopBar = () => (
  <div className="bg-navy text-white/75 text-xs">
    <div className="container-x flex h-9 items-center justify-between gap-4">
      <span className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 shrink-0 text-brand-blue" /> <span className="truncate"><span className="hidden sm:inline">Regulated Canadian Immigration Consultant </span>RCIC-IRB · Mississauga, ON</span></span>
      <span className="hidden items-center gap-2 md:flex"><Clock className="h-3.5 w-3.5" /> {HOURS}</span>
    </div>
  </div>
);

const ServicesMenu = () => (
  <div className="absolute left-1/2 top-full z-50 w-[820px] -translate-x-1/2 pt-3 opacity-0 invisible translate-y-1 transition-[opacity,transform,visibility] duration-200 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:visible group-focus-within:opacity-100" data-testid="services-dropdown">
    <div className="grid grid-cols-[1.7fr_1fr] overflow-hidden rounded-2xl border border-line bg-white shadow-[0_30px_70px_-20px_rgba(8,30,58,.35)]">
      <div className="p-6">
        <div className="grid grid-cols-2 gap-x-5 gap-y-5">
          {SERVICE_GROUPS.map((g) => (
            <div key={g}>
              <p className="eyebrow mb-2.5 text-[10px]">{g}</p>
              <div className="space-y-0.5">
                {SERVICES.filter((s) => s.group === g).map((s) => (
                  <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`nav-service-${s.slug}`} className="group/item flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] text-ink transition-colors hover:bg-cream">
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-cream text-brand-red transition-colors group-hover/item:bg-brand-red group-hover/item:text-white"><ServiceIcon name={s.icon} className="h-3.5 w-3.5" /></span>
                    <span className="leading-tight">{s.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
        <Link to="/services" className="mt-4 flex items-center justify-between border-t border-line px-2.5 pt-4 text-sm font-semibold text-navy hover:text-brand-red" data-testid="nav-all-services">
          View all immigration services <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="navy-depth relative flex flex-col justify-between p-6 text-white">
        <div>
          <p className="eyebrow is-light text-[10px]">Free first step</p>
          <p className="mt-3 font-display text-xl leading-snug">Not sure where to begin?</p>
          <p className="mt-2 text-sm text-white/70">Book a paid 15-minute consultation for a professional review of your options.</p>
        </div>
        <a href={bookingUrl()} target="_blank" rel="noopener noreferrer" className="btn-primary mt-6 justify-center text-sm"><CalendarCheck className="h-4 w-4" /> Book a Consultation</a>
      </div>
    </div>
  </div>
);

const navCls = ({ isActive }) => `relative px-3 py-2 text-sm font-semibold transition-colors ${isActive ? "text-brand-red" : "text-ink hover:text-brand-red"}`;

const MobileMenu = ({ onClose }) => (
  <div className="fixed inset-0 z-[60] overflow-y-auto bg-white lg:hidden" data-testid="mobile-menu">
    <div className="container-x flex h-16 items-center justify-between border-b border-line">
      <span className="font-display text-lg text-navy">Menu</span>
      <button onClick={onClose} aria-label="Close menu" data-testid="mobile-menu-close" className="p-2"><X className="h-6 w-6" /></button>
    </div>
    <nav className="container-x space-y-1 py-6">
      {[["/", "Home"], ["/eligibility-check", "Eligibility Check"], ["/free-consultation", "Free Consultation"], ["/about", "About"], ["/blog", "News"], ["/contact", "Contact"]].map(([to, l]) => (
        <Link key={to} to={to} className="block border-b border-line py-3 text-lg font-semibold text-navy" data-testid={`mobile-nav-${l.toLowerCase().replace(" ", "-")}`}>{l}</Link>
      ))}
      {SERVICE_GROUPS.map((g) => (
        <div key={g} className="pt-5">
          <p className="eyebrow mb-2">{g}</p>
          {SERVICES.filter((s) => s.group === g).map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} className="block py-2 text-[15px] text-ink" data-testid={`mobile-nav-service-${s.slug}`}>{s.name}</Link>
          ))}
        </div>
      ))}
      <div className="grid gap-3 pt-8">
        <a href={bookingUrl()} target="_blank" rel="noopener noreferrer" className="btn-primary justify-center" data-testid="mobile-nav-book">Book a Consultation</a>
        <a href={`tel:${PHONE_TEL}`} className="btn-outline justify-center"><Phone className="h-4 w-4" /> {PHONE}</a>
      </div>
    </nav>
  </div>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <>
      <TopBar />
      <header className={`sticky top-0 z-50 bg-white/90 backdrop-blur-md transition-[box-shadow,background-color] duration-300 ${scrolled ? "shadow-[0_10px_30px_-12px_rgba(8,30,58,.25)]" : "border-b border-line"}`} data-testid="site-header">
        <div className={`container-x flex items-center justify-between gap-4 transition-[height] duration-300 ${scrolled ? "h-[64px]" : "h-[80px]"}`}>
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link" aria-label="Chahal Overseas Immigration home">
            <img src="/assets/logo.webp" alt="Chahal Overseas Immigration logo" width="64" height="39" className={`w-auto transition-all duration-300 ${scrolled ? "h-9" : "h-11"}`} />
            <span className="text-[13px] font-bold leading-[1.1] tracking-wide text-navy">CHAHAL OVERSEAS<br /><span className="font-extrabold">IMMIGRATION</span></span>
          </Link>
          <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
            <NavLink to="/" end className={navCls} data-testid="nav-home">Home</NavLink>
            <div className="group relative">
              <NavLink to="/services" className={(p) => `${navCls(p)} inline-flex items-center gap-1`} data-testid="nav-services">Services <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" /></NavLink>
              <ServicesMenu />
            </div>
            <NavLink to="/eligibility-check" className={navCls} data-testid="nav-eligibility">Eligibility Check</NavLink>
            <NavLink to="/about" className={navCls} data-testid="nav-about">About</NavLink>
            <NavLink to="/blog" className={navCls} data-testid="nav-news">News</NavLink>
            <NavLink to="/contact" className={navCls} data-testid="nav-contact">Contact</NavLink>
          </nav>
          <div className="flex items-center gap-2">
            <a href={`tel:${PHONE_TEL}`} className="hidden items-center gap-2 rounded-full border border-line px-3.5 py-2 text-sm font-semibold text-navy transition-colors hover:border-navy/30 hover:text-brand-red xl:inline-flex" data-testid="header-phone"><Phone className="h-4 w-4 text-brand-red" /> {PHONE}</a>
            <a href={bookingUrl()} target="_blank" rel="noopener noreferrer" className="btn-primary !px-4 !py-2.5 text-sm" data-testid="header-book-btn"><CalendarCheck className="h-4 w-4" /><span className="sm:hidden">Book</span><span className="hidden sm:inline">Book a Consultation</span></a>
            <button className="p-2 text-navy lg:hidden" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="mobile-menu-btn"><Menu className="h-6 w-6" /></button>
          </div>
        </div>
      </header>
      {open && <MobileMenu onClose={() => setOpen(false)} />}
    </>
  );
};
